import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { getDashboardUrl } from './runtime-config';
import { readStoredAuthToken, readStoredOrgId } from './auth.service';

export interface InventoryApiItem {
  id: string;
  name: string;
  category: string;
  stock: number;
  price: number;
  currency: string;
  imageUrl: string;
}

type UnknownMap = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class InventoryApiService {
  private readonly base = `${getDashboardUrl()}/api/webapp/inventory`;

  constructor(private readonly http: HttpClient) {}

  async listInventory(): Promise<InventoryApiItem[]> {
    const orgId = readStoredOrgId();
    const response = await firstValueFrom(
      this.http.get<unknown>(`${this.base}/${encodeURIComponent(orgId)}`, { headers: this.buildHeaders() })
    );
    const rows = this.extractRows(response);
    return rows.map(row => this.toInventoryApiItem(row)).filter((row): row is InventoryApiItem => !!row);
  }

  async upsertInventory(inventoryId: string, item: InventoryApiItem): Promise<void> {
    const orgId = readStoredOrgId();
    await firstValueFrom(
      this.http.put(
        `${this.base}/${encodeURIComponent(orgId)}/${encodeURIComponent(inventoryId)}`,
        {
          productName: item.name,
          category: item.category,
          stock: item.stock,
          price: item.price,
          currency: item.currency,
          imageUrl: item.imageUrl || '',
          source: 'manual',
          sourceSheetUrl: '',
        },
        { headers: this.buildHeaders() }
      )
    );
  }

  async deleteInventory(inventoryId: string): Promise<void> {
    const orgId = readStoredOrgId();
    await firstValueFrom(
      this.http.delete(
        `${this.base}/${encodeURIComponent(orgId)}/${encodeURIComponent(inventoryId)}`,
        { headers: this.buildHeaders() }
      )
    );
  }

  private buildHeaders(): HttpHeaders {
    const token = readStoredAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return new HttpHeaders(headers);
  }

  private extractRows(payload: unknown): unknown[] {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object') {
      const source = payload as UnknownMap;
      if (Array.isArray(source['data'])) return source['data'];
      if (Array.isArray(source['items'])) return source['items'];
      if (Array.isArray(source['inventory'])) return source['inventory'];
    }
    return [];
  }

  private toInventoryApiItem(row: unknown): InventoryApiItem | null {
    if (!row || typeof row !== 'object') return null;
    const item = row as UnknownMap;
    const id = this.readString(item, ['inventoryId', 'id', 'productId', 'sku']);
    const name = this.readString(item, ['productName', 'name']);
    const category = this.readString(item, ['category']);
    const currency = this.readString(item, ['currency']) || 'USD';
    const imageUrl = this.readString(item, ['imageUrl']);
    const stock = this.readNumber(item, ['stock', 'quantity']);
    const price = this.readNumber(item, ['price', 'unitPrice']);

    if (!id || !name || !category) return null;
    return { id, name, category, currency, imageUrl, stock, price };
  }

  private readString(source: UnknownMap, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (typeof value === 'number') return String(value);
    }
    return '';
  }

  private readNumber(source: UnknownMap, keys: string[]): number {
    for (const key of keys) {
      const raw = source[key];
      const value = Number(raw);
      if (Number.isFinite(value)) return value;
    }
    return 0;
  }
}
